import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useAppContext } from '../../context/AppContext';
import { usePrayerTimes } from '../../hooks/usePrayerTimes';
import { useAmbientPersonalization } from '../../hooks/useAmbientPersonalization';
import { backgroundConfig } from '../../config/BackgroundConfig';
import {
  UnsplashImage,
  FALLBACK_IMAGE as UNSPLASH_FALLBACK_SENTINEL,
  fetchMosqueImage,
  getCachedImage,
  cacheImage,
  getImageUrl,
  isCacheValid,
  hasLoadedToday,
  markLoadedToday
} from '../../services/unsplashApi';
import './Background.css';
import { log, error as logError } from '../../utils/logger';
import { isNativeApp } from '../../utils/nativeApp';
import { onAppRefresh } from '../../services/appRefresh';

// Add a fallback image URL (replace with an actual URL if needed)
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1519817650390-64a93db51149?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2073&q=80';

export const Background: React.FC = () => {
  const { t } = useTranslation('common');
  const { colors } = useTheme();
  const { focus, focusCompleted, prayerCalculationMethod } = useAppContext();
  const { prayerTimes } = usePrayerTimes(prayerCalculationMethod);
  const { overlayColor } = useAmbientPersonalization({ focus, focusCompleted, prayerTimes });
  const [background, setBackground] = useState<UnsplashImage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [useFallback, setUseFallback] = useState<boolean>(false);

  // Add unique identifier for debugging
  const componentId = useRef(Math.random().toString(36).substr(2, 9));
  // Track if we've already loaded the background to prevent double-loading in StrictMode
  const hasLoaded = useRef(false);
  
  log(`Background component ${componentId.current} mounted`);

  // Load cached images or fetch new ones
  useEffect(() => {
    const currentId = componentId.current;
  log(`Background component ${currentId} useEffect running at ${new Date().toISOString()}`);
    
    // Prevent double-loading in React StrictMode
    if (hasLoaded.current) {
      log(`Background component ${currentId} already loaded, skipping`);
      return;
    }
    
    const loadBackground = async () => {
  log(`Background component ${currentId} loadBackground called`);
      hasLoaded.current = true;
      setLoading(true);
      
      try {
        // First, try to get cached image if it exists
        const cachedImage = await getCachedImage();
        if (cachedImage) {
          log(`Found cached image: ${cachedImage.id}`);

          // If cache is valid or we've already loaded today, use cached image
          if (isCacheValid() || await hasLoadedToday()) {
            log('Cache is valid or already loaded today, using cached image');
            setBackground(cachedImage);
            setLoading(false);
            setError(null);
            return;
          } else {
            log('Cache is expired and not loaded today, will fetch new image');
            // Continue to fetch new image below
          }
        }
        
        // No cached image found or cache expired and not loaded today
  log('No cached image or need to fetch new');
        
        // Check if we've already loaded today
        if (await hasLoadedToday()) {
          log('Already loaded today, but no valid cached image - using fallback');
          setUseFallback(true);
          setLoading(false);
          return;
        }
        
        // Haven't loaded today and no cached image, so fetch new one
  log('First load today, fetching new image from Unsplash API...');
        
        // Check if API key exists
        const apiKey = process.env.REACT_APP_UNSPLASH_API_KEY;
        if (!apiKey) {
          logError('Unsplash API key is missing. Using fallback image.');
          setUseFallback(true);
          setLoading(false);
          return;
        }
        
        const image = await fetchMosqueImage();
        
        // Cache the image if caching is enabled
        if (backgroundConfig.enableCache) {
          log('Caching image:', image.id);
          await cacheImage(image);
        }

        // Mark that we've loaded today
        await markLoadedToday();
        
        // Use the fetched image
        if (image) {
          log('Setting background from fetched image');
          setBackground(image);
          setError(null);
        } else {
          throw new Error('No image returned from API');
        }
      } catch (err) {
        logError('Error loading background:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setUseFallback(true);
      } finally {
        setLoading(false);
      }
    };

    loadBackground();
    
    // Set up timeout to change background at midnight
    let timeoutId: number | null = null;
    if (backgroundConfig.changeInterval > 0) {
      const now = new Date();
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      const timeUntilMidnight = midnight.getTime() - now.getTime();
      timeoutId = window.setTimeout(loadBackground, timeUntilMidnight);
  log(`Background will change in ${Math.round(timeUntilMidnight / (1000 * 60 * 60))} hours`);
    }
    
    // iOS native app: pull-to-refresh fetches a FRESH photo (same work as the
    // midnight rotation), keeping the current one on any failure. Dead code
    // elsewhere — the registry only runs from the native 'refresh' event.
    let offRefresh: (() => void) | undefined;
    if (isNativeApp()) {
      offRefresh = onAppRefresh(async () => {
        try {
          const image = await fetchMosqueImage();
          // fetchMosqueImage never throws — offline/API failure RESOLVES the
          // remote FALLBACK_IMAGE sentinel. Treat that as a failed refresh:
          // caching it would poison the daily cache (and markLoadedToday would
          // block a real fetch until tomorrow). Keep the current photo.
          if (image.id === UNSPLASH_FALLBACK_SENTINEL.id) {
            log('Pull-to-refresh got the fallback sentinel, keeping current photo');
            return;
          }
          if (backgroundConfig.enableCache) {
            await cacheImage(image);
          }
          await markLoadedToday();
          setBackground(image);
          setUseFallback(false);
          setError(null);
        } catch {
          // Only cacheImage/markLoadedToday (storage) can reach here — the
          // fetch itself resolves the sentinel instead of throwing.
          log('Pull-to-refresh background persist failed, keeping current photo');
        }
      });
    }

    return () => {
      offRefresh?.();
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        log(`Background component ${currentId} cleared timeout`);
      }
      log(`Background component ${currentId} useEffect cleanup`);
    };
  }, []);


  if (loading) {
    return (
      <div 
        className="background loading"
        style={{ backgroundColor: colors.background }}
      >
        <div className="background-loading-indicator">{t('background.loading')}</div>
      </div>
    );
  }

  // iOS native app: the photo fetch failed with no cached photo. The remote
  // FALLBACK_IMAGE and the flat error panel are both wrong here — the first is
  // itself a network dependency (dead offline), the second reads as breakage.
  // No bundled photograph exists in the repo, so paint the deterministic
  // offline gradient (Background.css) instead, keeping the ambient overlay.
  // NOTE: fetchMosqueImage never throws — offline it RESOLVES the remote
  // FALLBACK_IMAGE sentinel (id 'fallback-1'), which then paints nothing and
  // leaves the solid dark background this branch exists to fix — so the
  // sentinel counts as "no photo" here too (it can also arrive via the cache
  // if a previous offline open cached it). Extension surfaces are untouched:
  // this returns before their branches and isNativeApp() is hard-false
  // outside the WKWebView.
  if (
    isNativeApp() &&
    (useFallback || error || !background || background.id === UNSPLASH_FALLBACK_SENTINEL.id)
  ) {
    return (
      <div className="background-container">
        <div className="background-image background-image--bundled-fallback" />
        <div
          className="background-overlay"
          style={{ backgroundColor: overlayColor, transition: 'background-color 4s ease' }}
        />
      </div>
    );
  }

  // If using fallback image
  if (useFallback) {
    return (
      <div className="background-container">
        <div 
          className="background-image"
          style={{ 
            backgroundImage: `url(${FALLBACK_IMAGE})`,
          }}
        />
        <div
          className="background-overlay"
          style={{ backgroundColor: overlayColor, transition: 'background-color 4s ease' }}
        />
        <div className="background-attribution">
          {t('background.fallbackNotice')}
        </div>
      </div>
    );
  }

  if (error || !background) {
    return (
      <div 
        className="background error"
        style={{ backgroundColor: colors.background }}
      >
        <div className="background-error-message">
          {error ? t('background.error', { message: error.message }) : t('background.noImage')}
        </div>
      </div>
    );
  }

  return (
    <div className="background-container">
      <div 
        className="background-image"
        style={{ 
          backgroundImage: `url(${getImageUrl(background)})`,
        }}
      />
      <div
        className="background-overlay"
        style={{ backgroundColor: overlayColor, transition: 'background-color 4s ease' }}
      />
      <div className="background-attribution">
        <Trans
          i18nKey="background.photoByOnUnsplash"
          t={t}
          values={{ name: background.user.name }}
          components={{
            photographer: (
              <a
                key="photographer"
                href={`https://unsplash.com/@${background.user.username}?utm_source=muslim_dashboard&utm_medium=referral`}
                target="_blank"
                rel="noopener noreferrer"
              >{background.user.name}</a>
            ),
            unsplash: (
              <a
                key="unsplash"
                href="https://unsplash.com/?utm_source=muslim_dashboard&utm_medium=referral"
                target="_blank"
                rel="noopener noreferrer"
              >Unsplash</a>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default Background;